import type { NodeProps } from '@xyflow/react';

export interface PlaceholderNodeData {
  statusText?: string;
  prompt?: string;
  failed?: boolean;
  errorMsg?: string;
  resolving?: boolean;
  displayWidth: number;
  displayHeight: number;
}

function failureExplanation(error?:string):string {
  // Provider errors can contain prompts, URLs or internal details. Show only
  // fixed, human-readable explanations for known failure categories.
  if(/sensitive|content.?policy|safety|moderation|nsfw/i.test(error??''))return 'The image provider flagged this content as potentially sensitive. Try a different image or edit request.';
  if(/reject/i.test(error??''))return 'The image provider rejected this request.';
  if(/timed?\s*out|timeout/i.test(error??''))return 'The image provider took too long to respond.';
  return 'This slot did not produce an image.';
}

// v3.20 — the loading tile is now a SOLID, obviously-generating preview that
// stays "locked" in its slot until the real image is ready. It used to be a
// near-white (#F1F5F9) box with a faint grey icon that FADED to 0.4 opacity the
// moment its image started resolving — which read as an empty hole/gap in the
// grid mid-run (user: "weird gaps, holes… lock those previews in until the image
// is ready"). Now: a filled warm-grey tile, a clear spinner + "Generating…",
// full opacity throughout. The grey (#EAF0F7) matches ImageNode's own load
// backdrop so the placeholder → image hand-off is one continuous solid tile.
export function PlaceholderNode({ data }: NodeProps) {
  const d = data as unknown as PlaceholderNodeData;
  if (d.failed) return (
    <div role="status" aria-label="Image generation failed" data-testid="canvas-failed-slot" style={{width:d.displayWidth,height:d.displayHeight,borderRadius:8,border:'1.5px solid #E5C6C2',background:'#FBF2F0',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:8,padding:20,boxSizing:'border-box',textAlign:'center',overflow:'hidden'}}>
      <span aria-hidden="true" style={{width:24,height:24,border:'1.5px solid #BE7B73',borderRadius:'50%',color:'#A4574E',fontSize:16,lineHeight:'21px'}}>!</span>
      <span style={{fontSize:12,fontWeight:600,color:'#8F433C'}}>Image generation failed</span>
      <span style={{fontSize:11,lineHeight:1.45,color:'#94675F'}}>{failureExplanation(d.errorMsg)}</span>
    </div>
  );

  return (
    <div
      style={{
        width: d.displayWidth,
        height: d.displayHeight,
        borderRadius: 8,
        border: '1.5px solid #DCE3ED',
        background: '#EAF0F7',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        // Hold the tile solid — never fade to a faint "hole". The image node
        // renders on top with a matching grey and fades the ad in on load, so
        // the slot is a continuous solid preview from placeholder → image.
        opacity: 1,
      }}
    >
      {/* Shimmer sweep */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          border: '2.5px solid #C8D3E2',
          borderTopColor: '#5F6D82',
          animation: 'ph-spin 0.8s linear infinite',
          zIndex: 1,
        }}
      />
      <div style={{ fontSize: 11, color: '#5F6D82', fontWeight: 500, letterSpacing: '0.02em', zIndex: 1 }}>
        {d.statusText || 'Generating…'}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes ph-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
